
import { UseSelection } from '@/views/editor/composable/useSelection'
import { nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor'
import { useCommonStore } from '@/stores/common'
import { UseHistory } from '@/views/editor/composable/useHistory'
import { directSocketSave, deletePrefix } from '@/views/editor/composable/useNote'
import { getKeyCountManager } from '@/views/editor/composable/useEditorHelpers'
import { getNodeFromPath } from '@/views/editor/composable/useNoteUtils'
import { StyleModel } from './StyleModel'
import { restoreLatexElements } from '@/views/editor/composable/useLatexRestore'

/**
 * fontStyle.md 스펙 기반: execCommand 없이 span만으로 폰트 스타일을 적용/토글한다.
 * - span 중첩 금지
 * - span 전체 선택이면 span style만 변경
 * - span 부분 선택이면 기존 span을 분해하고(닫기 처리) 기존 스타일 + 신규 스타일을 합쳐 적용
 * - 단일 커서 + 동일 스타일 토글이면 커서 기준으로 span을 쪼개고 해당 스타일 제거
 *   - 남은 스타일이 있으면 그 스타일만 가진 span을 커서 위치에 두고(다음 입력에 적용)
 *   - 남은 스타일이 없으면 span 없이 커서만 위치
 */

export type FontStyleKey = 'bold' | 'italic' | 'underline' | 'line-through' | 'fontcolor' | 'backgroundcolor' | 'superscript' | 'subscript' | 'fontsize' | 'fontfamily'

let applyingStyleDepth = 0
let styleResolvers: (() => void)[] = []

export const isApplyingStyle = () => applyingStyleDepth > 0
export const setApplyingStyle = (value: boolean) => {
	if (value) {
		applyingStyleDepth++
	} else {
		applyingStyleDepth = Math.max(0, applyingStyleDepth - 1)
	}
	if (applyingStyleDepth === 0 && styleResolvers.length > 0) {
		const resolvers = [...styleResolvers]
		styleResolvers = []
		resolvers.forEach(resolve => resolve())
	}
}

export const waitForStyleComplete = (): Promise<void> => {
	if (applyingStyleDepth === 0) return Promise.resolve()
	return new Promise(resolve => {
		styleResolvers.push(resolve)
	})
}

const INVISIBLE = '\u2060' // WORD JOINER (삭제/동기화에서 사라지지 않는 보이지 않는 문자)
// socket 저장 시 섞이면 안 되는 보이지 않는 문자들(커서 고정/에디터 내부 처리용)
const INVISIBLE_RE = /[\u200B-\u200D\uFEFF\u00A0\u2060]/g
// 커서 마커용 INVISIBLE 문자만 (사용자 입력 스페이스 보존)
const INVISIBLE_MARKER_RE = /[\u200B-\u200D\uFEFF\u2060]/g

const sanitizeForSave = (html: string) => (html ? html.replace(INVISIBLE_RE, '') : html)

const saveBlockIfProject = (blockEl: HTMLElement | null) => {
	// md 규칙:
	// - noteType이 Project일 때만 socket.modifyNoteBlock 저장
	// - Template일 때는 socket를 타지 않아야 함
	// directSocketSave 내부에서 noteType을 검사해 socket을 타므로, 여기서는 content만 정리해서 전달한다.
	if (!blockEl) {
		try {
			directSocketSave()
		} catch (e) {}
		return
	}

	// 테이블 블록의 경우 저장하지 않음 (테이블은 별도의 셀 단위 저장 메커니즘 사용)
	if (blockEl.dataset.type === 'defaultTable') {
		return
	}

	try {
		directSocketSave('text', blockEl, sanitizeForSave(blockEl.innerHTML))
	} catch (e) {}
}

const emitBlockEditStart = (blockEl: HTMLElement | null) => {
	if (!blockEl || !blockEl.id) return
	const { noteUid, noteType } = storeToRefs(useEditorStore())
	if (noteType.value !== 'Project') return
	const { socket } = storeToRefs(useCommonStore())
	socket.value.blockEdit(noteUid.value, deletePrefix(blockEl.id))
}

const isEditorScope = (el: HTMLElement | null) => {
	if (!el) return false

	// Check if inside .editor--block or .editor--block__container
	const hasBlock = !!el.closest('.editor--block')
	const hasContainer = !!el.closest('.editor--block__container')

	// Check if inside table cell (td > div[contenteditable="true"])
	let current: HTMLElement | null = el
	let inTableCell = false
	while (current && current !== document.body) {
		if (current.tagName === 'DIV' && current.getAttribute('contenteditable') === 'true') {
			const parent = current.parentElement
			if (parent && parent.tagName === 'TD' && !parent.classList.contains('number--line')) {
				inTableCell = true
				break
			}
		}
		current = current.parentElement
	}

	return hasBlock || hasContainer || inTableCell
}

const isValidTableCell = (el: HTMLElement | null): boolean => {
	if (!el) return false

	const td = el.closest('td')
	if (!td || td.classList.contains('number--line')) return false

	const tableBlock = td.closest('.editor--block')
	if (!tableBlock || tableBlock.getAttribute('data-type') !== 'defaultTable') {
		return false
	}

	const editableDiv = td.querySelector('div[contenteditable="true"]') as HTMLElement | null
	if (!editableDiv) return false

	// el이 contenteditable div 내부이거나 div 자체인지 확인
	return editableDiv.contains(el) || editableDiv === el
}

export const createSpan = (text: string, cssText = '', className = ''): HTMLSpanElement => {
	const span = document.createElement('span')
	if (cssText) span.style.cssText = cssText
	if (className) span.className = className
	span.textContent = text
	return span
}

export const removeSpan = (span: HTMLSpanElement) => {
	const parent = span.parentNode
	if (!parent) return
	while (span.firstChild) parent.insertBefore(span.firstChild, span)
	parent.removeChild(span)
}

const normalizeCssText = (cssText: string) =>
	cssText
		.split(';')
		.map(s => s.trim())
		.filter(Boolean)
		.sort()
		.join(';')

export const isSameSpanSignature = (a: HTMLSpanElement, b: HTMLSpanElement) => {
	const modelA = StyleModel.fromSpan(a)
	const modelB = StyleModel.fromSpan(b)
	return modelA.equals(modelB)
}

export const mergeAdjacentSpans = (root: HTMLElement) => {
	const spans = Array.from(root.querySelectorAll('span'))
	for (const s of spans) {
		if (!s.parentElement) continue
		if (s.hasAttribute('data-cursor-marker')) continue
		// 선택 영역 마커 보호
		if (s.hasAttribute('data-selection-start-marker') || s.hasAttribute('data-selection-end-marker')) continue
		// LaTeX 요소 보호
		if (s.hasAttribute('data-latex') || s.closest('[data-latex]')) continue

		// 단일 커서 스타일 적용 케이스 처리
		const text = s.textContent || ''
		const model = StyleModel.fromSpan(s)

		// INVISIBLE 문자 뒤에 일반 텍스트가 있으면 INVISIBLE 제거 (사용자가 span 내부에 타이핑한 경우)
		// 단, 사용자가 입력한 스페이스(nbsp 등)는 보존
		if (!model.isEmpty() && text.includes(INVISIBLE) && text.length > 1) {
			const cleanText = text.replace(INVISIBLE_MARKER_RE, '')
			if (cleanText.length > 0 && cleanText !== text) {
				s.textContent = cleanText
				// 스타일 재적용 (verticalAlign은 유지)
				model.applyToSpan(s)
			}
		}

		const prev = s.previousSibling
		if (prev && prev.nodeType === Node.ELEMENT_NODE && (prev as Element).tagName === 'SPAN') {
			const p = prev as HTMLSpanElement
			if (p.hasAttribute('data-cursor-marker')) continue
			// 선택 영역 마커 보호
			if (p.hasAttribute('data-selection-start-marker') || p.hasAttribute('data-selection-end-marker')) continue
			// LaTeX 요소 보호
			if (p.hasAttribute('data-latex') || p.closest('[data-latex]')) continue

			if (isSameSpanSignature(p, s)) {
				// 병합: prev += current, StyleModel을 사용하여 스타일 재적용
				const model = StyleModel.fromSpan(p)
				p.textContent = (p.textContent || '') + (s.textContent || '')
				// 스타일 재적용하여 브라우저 정규화 문제 방지
				model.applyToSpan(p)
				s.remove()
			}
		}
	}
}

export const removeEmptySpans = (root: HTMLElement) => {
	const sel = window.getSelection()
	let cursorSpan: HTMLSpanElement | null = null

	if (sel && sel.rangeCount > 0) {
		const range = sel.getRangeAt(0)
		if (range.collapsed) {
			const node = range.startContainer
			if (node.nodeType === Node.TEXT_NODE && node.parentElement?.tagName === 'SPAN') {
				cursorSpan = node.parentElement as HTMLSpanElement
			} else if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'SPAN') {
				cursorSpan = node as HTMLSpanElement
			}
		}
	}

	const spans = Array.from(root.querySelectorAll('span'))
	spans.forEach(s => {
		if (s === cursorSpan) return
		if (s.hasAttribute('data-cursor-marker')) return
		// 선택 영역 마커 보호
		if (s.hasAttribute('data-selection-start-marker') || s.hasAttribute('data-selection-end-marker')) return
		// LaTeX 요소 보호
		if (s.hasAttribute('data-latex') || s.closest('[data-latex]')) return

		const text = s.textContent || ''
		if (text === '' || text.match(/^[\u200B-\u200D\uFEFF\u00A0\u2060]+$/)) {
			s.remove()
		}
	})
}

// span 중첩 금지: <span(outer)><span(inner)>..</span></span> 형태를 평탄화한다.
// - outer 스타일은 텍스트/inner span에 병합되어 적용된다.
export const flattenNestedSpans = (root: HTMLElement) => {
	const allSpans = Array.from(root.querySelectorAll('span'))
	// 안쪽부터 처리하기 위해 깊이 내림차순
	allSpans.sort((a, b) => {
		const da = (a.closest('span') ? 1 : 0) + a.querySelectorAll('span').length
		const db = (b.closest('span') ? 1 : 0) + b.querySelectorAll('span').length
		return db - da
	})

	allSpans.forEach(outer => {
		if (outer.hasAttribute('data-latex') || outer.closest('[data-latex]')) return
		const hasInnerSpan = !!outer.querySelector('span')
		if (!hasInnerSpan) return

		const outerModel = StyleModel.fromSpan(outer)
		const outerClass = outer.className || ''

		const mergeClass = (a: string, b: string) => {
			const set = new Set<string>()
			a.split(/\s+/)
				.filter(Boolean)
				.forEach(c => set.add(c))
			b.split(/\s+/)
				.filter(Boolean)
				.forEach(c => set.add(c))
			return Array.from(set).join(' ')
		}

		const frag = document.createDocumentFragment()
		Array.from(outer.childNodes).forEach(node => {
			if (node.nodeType === Node.TEXT_NODE) {
				const t = node.textContent || ''
				if (t === '') return
				const newSpan = createSpan(t, '', outerClass)
				outerModel.applyToSpan(newSpan)
				frag.appendChild(newSpan)
				return
			}
			if (node.nodeType === Node.ELEMENT_NODE) {
				const el = node as HTMLElement
				if (el.tagName === 'SPAN') {
					const inner = el as HTMLSpanElement
					// 선택 영역 마커는 그대로 유지 (병합하지 않음, 복제하지 않고 이동)
					if (inner.hasAttribute('data-selection-start-marker') || inner.hasAttribute('data-selection-end-marker')) {
						frag.appendChild(inner)
					} else if (inner.hasAttribute('data-latex')) {
						frag.appendChild(inner)
					} else {
						const innerModel = StyleModel.fromSpan(inner)
						const mergedModel = outerModel.merge(innerModel)
						const mergedClass = mergeClass(outerClass, inner.className || '')
						const newSpan = createSpan(inner.textContent || '', '', mergedClass)
						mergedModel.applyToSpan(newSpan)
						// 마커/데이터셋은 inner 우선으로 유지
						Array.from(inner.attributes).forEach(attr => {
							if (attr.name === 'style' || attr.name === 'class') return
							newSpan.setAttribute(attr.name, attr.value)
						})
						frag.appendChild(newSpan)
					}
				} else {
					// span이 아닌 요소는 그대로 두되, outer 스타일을 잃지 않도록 내부 텍스트를 span으로 래핑하는 것은 normalize에서 처리
					frag.appendChild(el.cloneNode(true))
				}
				return
			}
			frag.appendChild(node.cloneNode(true))
		})

		outer.replaceWith(frag)
	})
}

const isStylelessSpan = (span: HTMLSpanElement) => {
	const model = StyleModel.fromSpan(span)
	const isEmpty = model.isEmpty()

	if (!isEmpty && !model.verticalAlign && model.fontSize) {
		const blockEl = span.closest('.editor--block') as HTMLElement | null
		if (blockEl) {
			const computed = window.getComputedStyle(blockEl)
			const baseFontSize = parseFloat(computed.fontSize) || 14
			const currentFontSize = parseFloat(model.fontSize)
			const expectedSize = Math.round(baseFontSize * 0.75 * 10) / 10

			if (Math.abs(currentFontSize - expectedSize) < 0.1) {
				if (model.originalFontSize) {
					span.style.setProperty('font-size', model.originalFontSize)
					span.removeAttribute('data-original-fontsize')
				} else {
					span.style.removeProperty('font-size')
				}
				const updatedModel = StyleModel.fromSpan(span)
				return updatedModel.isEmpty()
			}
		}
	}

	return isEmpty
}

// 규칙: 스타일이 없는 span은 제거한다.
// - 단, text가 있을 경우 span 태그만 사라져야 하고(text는 TextNode로 유지)
export const unwrapStylelessSpans = (root: HTMLElement) => {
	const spans = Array.from(root.querySelectorAll('span'))
	spans.forEach(s => {
		const span = s as HTMLSpanElement
		if (span.hasAttribute('data-cursor-marker')) return
		// 선택 영역 마커 보호 (스타일 없어도 제거하면 안 됨)
		if (span.hasAttribute('data-selection-start-marker') || span.hasAttribute('data-selection-end-marker')) return
		// LaTeX 요소 보호
		if (span.hasAttribute('data-latex') || span.closest('[data-latex]')) return
		if (!isStylelessSpan(span)) return
		// 내용이 없으면 그냥 제거
		if ((span.textContent || '') === '') {
			span.remove()
			return
		}
		removeSpan(span) // unwrap => 텍스트는 TextNode로 유지됨
	})
}

// verticalAlign 없이 fontSize만 남은 고아 span 정리
const cleanupOrphanedFontSize = (root: HTMLElement) => {
	const spans = Array.from(root.querySelectorAll('span'))
	spans.forEach(s => {
		const span = s as HTMLSpanElement
		if (span.hasAttribute('data-cursor-marker')) return
		if (span.hasAttribute('data-selection-start-marker') || span.hasAttribute('data-selection-end-marker')) return
		// LaTeX 요소 보호
		if (span.hasAttribute('data-latex') || span.closest('[data-latex]')) return

		// verticalAlign이 없는데 fontSize만 있는 경우 (DOM에서 직접 확인)
		const hasVerticalAlign = span.style.verticalAlign === 'super' || span.style.verticalAlign === 'sub'
		const hasFontSize = !!span.style.fontSize
		const hasOriginalFontSize = span.hasAttribute('data-original-fontsize')

		if (!hasVerticalAlign && hasOriginalFontSize) {
			const originalFontSize = span.getAttribute('data-original-fontsize')
			span.removeAttribute('data-original-fontsize')
			if (originalFontSize && originalFontSize !== '') {
				span.style.setProperty('font-size', originalFontSize)
			} else {
				span.style.removeProperty('font-size')
			}
			return
		}

		if (!hasVerticalAlign && hasFontSize) {
			const blockEl = span.closest('.editor--block') as HTMLElement | null
			if (blockEl) {
				const computed = window.getComputedStyle(blockEl)
				const baseFontSize = parseFloat(computed.fontSize) || 14
				const spanFontSize = parseFloat(span.style.fontSize)
				const expectedSize = Math.round(baseFontSize * 0.75 * 10) / 10

				if (Math.abs(spanFontSize - expectedSize) < 0.1) {
					span.style.removeProperty('font-size')
				}
			}
		}
	})
}

const clearEmptySpanStyles = (root: HTMLElement) => {
	const hadClearedStyles = root.hasAttribute('data-cleared-styles')

	if (hadClearedStyles) {
		root.removeAttribute('data-cleared-styles')
		const spans = Array.from(root.querySelectorAll('span'))
		spans.forEach(s => {
			if (s.hasAttribute('data-cursor-marker')) return
			if (s.hasAttribute('data-selection-start-marker') || s.hasAttribute('data-selection-end-marker')) return
			if (s.hasAttribute('data-latex') || s.closest('[data-latex]')) return

			const model = StyleModel.fromSpan(s)
			if (!model.isEmpty()) {
				s.style.cssText = ''
				s.className = ''
				s.removeAttribute('data-editor-bg')
				s.removeAttribute('data-original-fontsize')
			}
		})
		return
	}

	let didClear = false
	const spans = Array.from(root.querySelectorAll('span'))
	spans.forEach(s => {
		if (s.hasAttribute('data-cursor-marker')) return
		if (s.hasAttribute('data-selection-start-marker') || s.hasAttribute('data-selection-end-marker')) return
		if (s.hasAttribute('data-latex') || s.closest('[data-latex]')) return

		const text = s.textContent || ''
		if (text === '' || text.match(/^[\u200B-\u200D\uFEFF\u00A0\u2060]+$/)) {
			const model = StyleModel.fromSpan(s)
			if (!model.isEmpty()) {
				s.style.cssText = ''
				s.className = ''
				s.removeAttribute('data-editor-bg')
				s.removeAttribute('data-original-fontsize')
				didClear = true
			}
		}
	})

	if (didClear) {
		const blockText = root.textContent?.replace(/[\u200B-\u200D\uFEFF\u2060\u00A0\s]/g, '') || ''
		if (blockText === '') {
			root.setAttribute('data-cleared-styles', 'true')
		}
	}
}

export const normalizeBlock = (blockEl: HTMLElement) => {
	flattenNestedSpans(blockEl)
	mergeAdjacentSpans(blockEl)
	cleanupOrphanedFontSize(blockEl)
	clearEmptySpanStyles(blockEl)
	unwrapStylelessSpans(blockEl)
	removeEmptySpans(blockEl)
	cleanupOrphanedFontSize(blockEl)
	restoreLatexElements(blockEl)
}

// Get the appropriate element to normalize (cell div for tables, block for others)
const getNormalizeTarget = (blockEl: HTMLElement | null, anchorEl: HTMLElement | null): HTMLElement | null => {
	if (!blockEl) return null
	if (blockEl.dataset.type === 'defaultTable' && anchorEl) {
		const cellDiv = anchorEl.closest('div[contenteditable="true"]') as HTMLElement | null
		return cellDiv || blockEl
	}
	return blockEl
}

const buildRangeFromAbsOffsets = (root: HTMLElement, startAbs: number, endAbs: number): Range | null => {
	if (!root) return null
	const start = Math.max(0, startAbs)
	const end = Math.max(0, endAbs)
	if (start === end) return null

	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
	let current = 0
	let startNode: Text | null = null
	let endNode: Text | null = null
	let startOffset = 0
	let endOffset = 0
	let lastTextNode: Text | null = null

	let node = walker.nextNode() as Text | null
	while (node) {
		const text = node.textContent || ''
		const len = text.length
		lastTextNode = node

		if (!startNode && current + len >= start) {
			startNode = node
			startOffset = Math.max(0, Math.min(start - current, len))
		}
		if (current + len >= end) {
			endNode = node
			endOffset = Math.max(0, Math.min(end - current, len))
			break
		}

		current += len
		node = walker.nextNode() as Text | null
	}

	// 범위가 텍스트 끝을 넘어간 경우 마지막 텍스트 노드로 보정
	if (startNode && !endNode && lastTextNode) {
		endNode = lastTextNode
		endOffset = lastTextNode.textContent?.length || 0
	}

	if (!startNode || !endNode) return null

	const range = document.createRange()
	range.setStart(startNode, startOffset)
	range.setEnd(endNode, endOffset)
	return range
}

const isSameBlockUid = (blockEl: HTMLElement | null, uid: string | null | undefined) => {
	if (!blockEl || !uid) return false
	const id = blockEl.id || ''
	if (!id) return false
	if (id === uid) return true
	if (id === `prefix-${uid}`) return true
	if (id === `temp-${uid}`) return true
	return false
}

const hasStyle = (span: HTMLSpanElement, style: FontStyleKey, value = '') => {
	const model = StyleModel.fromSpan(span)
	return model.hasStyle(style, value)
}

const applyStyleToSpan = (span: HTMLSpanElement, style: FontStyleKey, value = '') => {
	const model = StyleModel.fromSpan(span)
	const updated = model.apply(style, value)
	updated.applyToSpan(span)
}

const removeStyleFromSpan = (span: HTMLSpanElement, style: FontStyleKey, value = '') => {
	const model = StyleModel.fromSpan(span)
	const updated = model.remove(style, value)
	updated.applyToSpan(span)
}

const getClosestSpan = (node: Node | null): HTMLSpanElement | null => {
	if (!node) return null
	const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as any as HTMLElement)
	if (!el) return null
	return (el.closest && (el.closest('span') as HTMLSpanElement | null)) || null
}

const setCaretBetweenNodes = (beforeNode: Node, afterNode: Node) => {
	const sel = window.getSelection()
	if (!sel) return
	const r = document.createRange()
	r.setStartAfter(beforeNode)
	r.setEndBefore(afterNode)
	r.collapse(true)
	sel.removeAllRanges()
	sel.addRange(r)
}

const setCaretInsideText = (textNode: Text, offset: number) => {
	const sel = window.getSelection()
	if (!sel) return
	const r = document.createRange()
	r.setStart(textNode, Math.max(0, Math.min(offset, textNode.length)))
	r.collapse(true)
	sel.removeAllRanges()
	sel.addRange(r)
}

type SpanSignature = { model: StyleModel; attrs: Record<string, string> }

const isSignatureEmpty = (sig: SpanSignature) => sig.model.isEmpty()

const mergeSignature = (a: SpanSignature, b: SpanSignature): SpanSignature => {
	return {
		model: a.model.merge(b.model),
		attrs: { ...a.attrs, ...b.attrs },
	}
}

const signatureFromSpan = (span: HTMLSpanElement): SpanSignature => {
	const attrs: Record<string, string> = {}
	Array.from(span.attributes).forEach(attr => {
		if (attr.name === 'style' || attr.name === 'class') return
		attrs[attr.name] = attr.value
	})
	return { model: StyleModel.fromSpan(span), attrs }
}

/**
 * extractContents()로 뽑아낸 fragment를 "span-only + 중첩 없는" 형태로 다시 구성하면서
 * 선택된 텍스트에 style을 토글(추가/삭제)한다.
 */
const buildStyledFragmentFromExtracted = (extracted: DocumentFragment, style: FontStyleKey, value: string, fallbackSig: SpanSignature): { frag: DocumentFragment; lastNode: Node | null } => {
	// fallbackSig를 무시하고 항상 빈 signature로 시작 (각 span이 자신의 스타일만 유지)
	const emptyFallback: SpanSignature = { model: new StyleModel(), attrs: {} }

	const out = document.createDocumentFragment()
	let lastNode: Node | null = null

	const applyAttrs = (span: HTMLSpanElement, sig: SpanSignature) => {
		Object.entries(sig.attrs).forEach(([k, v]) => span.setAttribute(k, v))
	}

	const appendSelectedText = (text: string, sig: SpanSignature) => {
		if (!text) return

		let model = sig.model.clone()
		if (style === 'superscript' || style === 'subscript') {
			model.fontSize = null
		}

		const s = document.createElement('span')
		s.textContent = text
		model.applyToSpan(s)
		applyAttrs(s, sig)

		// 값 스타일(fontsize, fontcolor 등)은 항상 적용, 토글 스타일(bold, italic 등)은 토글
		const isValueStyle = style === 'fontsize' || style === 'fontcolor' || style === 'backgroundcolor' || style === 'fontfamily'

		if (isValueStyle) {
			// 값 스타일은 항상 덮어쓰기
			model = model.apply(style, value)
		} else {
			// 토글 스타일은 있으면 제거, 없으면 추가
			if (model.hasStyle(style, value)) {
				model = model.remove(style, value)
			} else {
				model = model.apply(style, value)
			}
		}
		model.applyToSpan(s)

		// 규칙: 스타일이 없는 span은 제거(텍스트는 TextNode로 유지)
		if (model.isEmpty()) {
			const tn = document.createTextNode(text)
			out.appendChild(tn)
			lastNode = tn
			return
		}
		out.appendChild(s)
		lastNode = s
	}

	const walk = (node: Node, inherited: SpanSignature) => {
		if (node.nodeType === Node.TEXT_NODE) {
			const t = node.textContent || ''
			if (t === '') return
			appendSelectedText(t, inherited)
			return
		}

		if (node.nodeType === Node.ELEMENT_NODE) {
			const el = node as HTMLElement
			if (el.tagName === 'BR') {
				const br = document.createElement('br')
				out.appendChild(br)
				lastNode = br
				return
			}
			if (el.tagName === 'SPAN') {
				const sig = signatureFromSpan(el as HTMLSpanElement)
				Array.from(el.childNodes).forEach(ch => walk(ch, sig))
				return
			}
			// span이 아닌 요소는 텍스트 단위로 분해 (중첩 span 방지)
			Array.from(el.childNodes).forEach(ch => walk(ch, emptyFallback))
			return
		}
	}

	Array.from(extracted.childNodes).forEach(n => walk(n, emptyFallback))

	return { frag: out, lastNode }
}

/**
 * 단일 커서 + 동일 스타일 토글: carrier span을 [before][(remainSpan?)][after]로 분해
 */
const splitAtCaretAndToggleOff = (carrier: HTMLSpanElement, range: Range, style: FontStyleKey, value = '') => {
	const full = carrier.textContent || ''

	let cutIndex = 0
	try {
		const pre = document.createRange()
		pre.setStart(carrier, 0)
		pre.setEnd(range.startContainer, range.startOffset)
		cutIndex = pre.toString().length
	} catch (e) {
		// fallback: 가장 흔한 단일 텍스트 노드 케이스
		if (range.startContainer.nodeType === Node.TEXT_NODE && carrier.firstChild === range.startContainer) {
			cutIndex = range.startOffset
		}
	}
	cutIndex = Math.max(0, Math.min(cutIndex, full.length))

	const beforeText = full.slice(0, cutIndex)
	const afterText = full.slice(cutIndex)

	const carrierModel = StyleModel.fromSpan(carrier)
	const parent = carrier.parentNode
	if (!parent) return

	const frag = document.createDocumentFragment()

	// before 부분: 원본 스타일 유지
	if (beforeText) {
		const beforeSpan = document.createElement('span')
		beforeSpan.textContent = beforeText
		carrierModel.applyToSpan(beforeSpan)
		frag.appendChild(beforeSpan)
	}

	// remain 부분: 스타일 제거 후 남은 스타일만
	const remainModel = carrierModel.remove(style, value)
	const cursorSpan = document.createElement('span')
	cursorSpan.textContent = INVISIBLE
	cursorSpan.setAttribute('data-cursor-marker', 'true')
	if (!remainModel.isEmpty()) {
		remainModel.applyToSpan(cursorSpan)
	}
	frag.appendChild(cursorSpan)

	// after 부분: 원본 스타일 유지 (커서 뒤 텍스트는 영향받지 않음)
	if (afterText) {
		const afterSpan = document.createElement('span')
		afterSpan.textContent = afterText
		carrierModel.applyToSpan(afterSpan)
		frag.appendChild(afterSpan)
	}

	// carrier 교체
	parent.replaceChild(frag, carrier)
}

const isBlockAvatarLocked = (el: HTMLElement | null): boolean => {
	if (!el) return false
	const block = el.closest('.editor--block') as HTMLElement | null
	if (block?.hasAttribute('data-avatar-locked')) return true
	const container = block?.closest('.editor--block__container') as HTMLElement | null
	if (container?.querySelector('.avatar--container')) return true
	const td = el.closest('td')
	if (td) {
		const cellDiv = td.querySelector('div[contenteditable]') as HTMLElement | null
		if (cellDiv?.hasAttribute('data-avatar-locked')) return true
	}
	return false
}

/**
 * 선택 범위에 스타일을 적용/토글한다.
 */
export const applyFontStyle = async (style: FontStyleKey, value = ''): Promise<boolean> => {
	setApplyingStyle(true)
	try {
		const { undoStack } = storeToRefs(useEditorStore())
		const lastState = undoStack.value[undoStack.value.length - 1]
		const recentBeforePush = lastState?.metadata?.fontStyleApplied && (Date.now() - (lastState.timestamp || 0) < 200)

		if (!recentBeforePush) {
			try {
				const { getGlobalTypingHistoryManager } = await import('@/views/editor/composable/table/useTable.ts')
				const typingManager = getGlobalTypingHistoryManager()
				if (typingManager) {
					await typingManager.saveCurrentState()
				}
			} catch (e) {}
			const history = new UseHistory()
			await history.push(false, { preserveSelectionOnUndo: true, fontStyleApplied: true })
		}

		return await applyFontStyleInternal(style, value)
	} finally {
		setApplyingStyle(false)
	}
}

const applyFontStyleInternal = async (style: FontStyleKey, value = ''): Promise<boolean> => {
	const keyCountManager = getKeyCountManager()
	// keyCountManager.resetKeyCount()  // ← 이중 리셋 방지: Before 저장 직후 리셋하면 After와 다음 입력 사이 간격이 벌어짐. After 저장 직후(Line 1747)에만 리셋함
	keyCountManager.handleCompositionEnd()

	const activeElement = document.activeElement
	if (activeElement && activeElement instanceof HTMLElement) {
		const compositionEvent = new CompositionEvent('compositionend', {
			data: '',
			bubbles: true,
			cancelable: true,
		})
		activeElement.dispatchEvent(compositionEvent)
		await nextTick()
	}

	let markerRestoredSuccessfully = false
	let sel = window.getSelection()
	if (!sel || sel.rangeCount === 0) {
		return false
	}
	let range = sel.getRangeAt(0)

	// 현재 selection이 editor 범위 밖이면 changeRange에서 복원 시도
	// range.startContainer 기준으로 요소 찾기 (sel.anchorNode는 신뢰할 수 없음)
	let anchorEl = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : (range.startContainer as HTMLElement | null)
	let focusEl = range.endContainer.nodeType === Node.TEXT_NODE ? range.endContainer.parentElement : (range.endContainer as HTMLElement | null)

	// 파일 컨테이너 영역에서는 스타일 적용 차단
	if (anchorEl?.closest('.file--container') || focusEl?.closest('.file--container')) {
		return false
	}

	// 실시간 수정으로 잠긴 블럭에는 스타일 적용 차단
	if (isBlockAvatarLocked(anchorEl) || isBlockAvatarLocked(focusEl)) {
		return false
	}

	// Check if we're in a table cell (not table block level)
	const isInTableCell = (el: HTMLElement | null): boolean => {
		if (!el) return false
		const td = el.closest('td')
		if (!td || td.classList.contains('number--line')) return false
		// TD가 있고, 그 TD가 .editor--block 안에 있는지 확인
		const tableBlock = td.closest('.editor--block')
		return !!tableBlock && tableBlock.getAttribute('data-type') === 'defaultTable'
	}

	const anchorInTableCell = isInTableCell(anchorEl)
	const focusInTableCell = isInTableCell(focusEl)

	// Get blocks for later use
	const anchorBlock = anchorEl?.closest('.editor--block') as HTMLElement | null
	const focusBlock = focusEl?.closest('.editor--block') as HTMLElement | null

	const { changeRange, selector } = storeToRefs(useEditorStore())

	// For table blocks, try to restore from selector (last focused block)
	// Tables now use the general restoration logic (since UseSelection.ts correctly scopes to Cells now)
	// Only run restoration if regular scope check fails
	const isAnchorInScope = isEditorScope(anchorEl)
	const isFocusInScope = isEditorScope(focusEl)

	// 멀티블록 선택 감지: 복원 전에 확인
	const isMultiBlockBeforeRestore = anchorBlock && focusBlock && anchorBlock !== focusBlock

	// 선택 영역이 유효한지 확인 (텍스트가 실제로 선택되어 있는지)
	const hasValidSelection = !range.collapsed && range.toString().length > 0

	if (!anchorInTableCell && !focusInTableCell) {
		// Allow restoration for Tables too (removed selector.value?.dataset.type !== 'defaultTable' check)
		// 멀티블록 선택일 때는 복원하지 않음 (복원 시 단일 블록으로 축소됨)
		// 유효한 선택 영역이 있으면 절대 복원하지 않음 (ColorPalette에서 savedRange로 복원하므로)
		if (!hasValidSelection && (!isAnchorInScope || !isFocusInScope) && !isMultiBlockBeforeRestore) {
			if (changeRange.value && changeRange.value.blockUid) {
				try {
					const blockId = changeRange.value.blockUid.includes('prefix-')
						? changeRange.value.blockUid
						: changeRange.value.blockUid.includes('temp-')
							? changeRange.value.blockUid
							: `prefix-${changeRange.value.blockUid}`
					let block = document.getElementById(blockId) as HTMLElement | null

					// blockUid로 블록을 찾지 못했으면 selector를 사용
					if (!block && selector.value) {
						block = selector.value
					}

					// 멀티블록 선택 복원: endBlockUid가 있고 blockUid와 다르면
					const hasMultiBlockSelection = changeRange.value.endBlockUid && changeRange.value.endBlockUid !== changeRange.value.blockUid

					if (hasMultiBlockSelection) {
						const endBlockId = changeRange.value.endBlockUid.includes('prefix-')
							? changeRange.value.endBlockUid
							: changeRange.value.endBlockUid.includes('temp-')
								? changeRange.value.endBlockUid
								: `prefix-${changeRange.value.endBlockUid}`
						const endBlock = document.getElementById(endBlockId) as HTMLElement | null

						if (block && endBlock && changeRange.value.startAbs !== undefined && changeRange.value.endAbs !== undefined) {
							// 시작 블록에서 텍스트 노드와 오프셋 찾기
							const startWalker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT)
							let startNode: Text | null = null
							let startOffset = 0
							let accumulatedStart = 0

							while (startWalker.nextNode()) {
								const textNode = startWalker.currentNode as Text
								const textLength = textNode.textContent?.length || 0
								if (accumulatedStart + textLength >= changeRange.value.startAbs) {
									startNode = textNode
									startOffset = changeRange.value.startAbs - accumulatedStart
									break
								}
								accumulatedStart += textLength
							}

							// 끝 블록에서 텍스트 노드와 오프셋 찾기
							const endWalker = document.createTreeWalker(endBlock, NodeFilter.SHOW_TEXT)
							let endNode: Text | null = null
							let endOffset = 0
							let accumulatedEnd = 0

							while (endWalker.nextNode()) {
								const textNode = endWalker.currentNode as Text
								const textLength = textNode.textContent?.length || 0
								if (accumulatedEnd + textLength >= changeRange.value.endAbs) {
									endNode = textNode
									endOffset = changeRange.value.endAbs - accumulatedEnd
									break
								}
								accumulatedEnd += textLength
							}

							if (startNode && endNode) {
								const newRange = document.createRange()
								newRange.setStart(startNode, startOffset)
								newRange.setEnd(endNode, endOffset)

								sel.removeAllRanges()
								sel.addRange(newRange)
								range = newRange

								anchorEl = startNode.parentElement
								focusEl = endNode.parentElement
							}
						}

						// 복원 후 editor scope 체크는 건너뛰고 계속 진행
					} else if (block) {
						const startAbs = changeRange.value.startAbs
						const endAbs = changeRange.value.endAbs
						let restoredByAbs = false

						if (block.dataset.type !== 'defaultTable' && typeof startAbs === 'number' && typeof endAbs === 'number' && startAbs !== endAbs) {
							const absRange = buildRangeFromAbsOffsets(block, startAbs, endAbs)
							if (absRange) {
								sel.removeAllRanges()
								sel.addRange(absRange)
								range = absRange
								anchorEl = absRange.startContainer.nodeType === Node.TEXT_NODE ? absRange.startContainer.parentElement : (absRange.startContainer as HTMLElement | null)
								focusEl = absRange.endContainer.nodeType === Node.TEXT_NODE ? absRange.endContainer.parentElement : (absRange.endContainer as HTMLElement | null)
								restoredByAbs = true
							}
						}

						if (restoredByAbs) {
							// 절대 오프셋 복원 성공 시 nodePath 복원은 생략
						} else if (changeRange.value.nodePath && changeRange.value.endNodePath) {
							// 단일 블록 선택 복원
							const startNode = getNodeFromPath(block, changeRange.value.nodePath)
							const endNode = getNodeFromPath(block, changeRange.value.endNodePath)

							// getNodeFromPath가 잘못된 노드(TR 등)를 반환하면 테이블 블록용 대체 로직 실행
							if (startNode && endNode && startNode.nodeType !== Node.TEXT_NODE && startNode.nodeName === 'TR' && block.dataset.type === 'defaultTable') {
								// changeRange의 cellUid로 TD 찾기
								const cellUid = changeRange.value?.cellUid
								let targetCell: HTMLElement | null = null

								if (cellUid) {
									const cellId = cellUid.includes('prefix-') ? cellUid : `prefix-${cellUid}`
									const cellEl = document.getElementById(cellId)
									if (cellEl && cellEl.tagName === 'TD') {
										targetCell = cellEl.querySelector('div[contenteditable="true"]') as HTMLElement | null
									}
								}

								// cellUid로 못 찾으면 첫 번째 contenteditable div 사용
								if (!targetCell) {
									targetCell = block.querySelector('td:not(.number--line) div[contenteditable="true"]') as HTMLElement | null
								}

								if (targetCell) {
									// startAbs와 endAbs를 사용하여 정확한 선택 영역 복원
									const startAbs = changeRange.value?.startAbs
									const endAbs = changeRange.value?.endAbs

									if (typeof startAbs === 'number' && typeof endAbs === 'number' && startAbs !== endAbs) {
										// 절대 오프셋을 사용하여 range 생성
										const absRange = buildRangeFromAbsOffsets(targetCell, startAbs, endAbs)
										if (absRange) {
											sel.removeAllRanges()
											sel.addRange(absRange)
											range = absRange
											anchorEl = absRange.startContainer.nodeType === Node.TEXT_NODE ? absRange.startContainer.parentElement : (absRange.startContainer as HTMLElement | null)
											focusEl = absRange.endContainer.nodeType === Node.TEXT_NODE ? absRange.endContainer.parentElement : (absRange.endContainer as HTMLElement | null)
										}
									} else {
										// fallback: 전체 셀 선택
										const walker = document.createTreeWalker(targetCell, NodeFilter.SHOW_TEXT)
										const textNodes: Text[] = []
										let node: Node | null
										while ((node = walker.nextNode())) {
											if (node.textContent && node.textContent.trim()) {
												textNodes.push(node as Text)
											}
										}

										if (textNodes.length > 0) {
											const newRange = document.createRange()
											const firstNode = textNodes[0]
											const lastNode = textNodes[textNodes.length - 1]
											newRange.setStart(firstNode, 0)
											newRange.setEnd(lastNode, lastNode.textContent?.length || 0)

											sel.removeAllRanges()
											sel.addRange(newRange)
											range = newRange
											anchorEl = firstNode.parentElement
											focusEl = lastNode.parentElement
										}
									}
								}
							} else if (startNode && endNode) {
								const newRange = document.createRange()

								const startOffset = Math.max(0, changeRange.value.offset - 1)
								const endOffset = Math.max(0, changeRange.value.endOffset - 1)

								const startMaxOffset = startNode.nodeType === Node.TEXT_NODE ? startNode.textContent?.length || 0 : startNode.childNodes.length || 0
								const endMaxOffset = endNode.nodeType === Node.TEXT_NODE ? endNode.textContent?.length || 0 : endNode.childNodes.length || 0

								newRange.setStart(startNode, Math.min(startOffset, startMaxOffset))
								newRange.setEnd(endNode, Math.min(endOffset, endMaxOffset))

								if (sel) {
									sel.removeAllRanges()
									sel.addRange(newRange)
									range = newRange

									// Re-check anchor and focus elements from range (not from sel)
									anchorEl = newRange.startContainer.nodeType === Node.TEXT_NODE ? newRange.startContainer.parentElement : (newRange.startContainer as HTMLElement | null)
									focusEl = newRange.endContainer.nodeType === Node.TEXT_NODE ? newRange.endContainer.parentElement : (newRange.endContainer as HTMLElement | null)
								}
							}
						}
					}
				} catch (e) {}
			}
		}

		// 복원 후에도 editor scope 밖이면 종료
		if (!isEditorScope(anchorEl) || !isEditorScope(focusEl)) {
			return false
		}
	}

	// DOM 변경 전 커서 저장 (모든 컨텍스트에서 저장 - 복원 방식만 다름)
	new UseSelection().setChangeSelection()

	// 복원 후 anchorBlock과 focusBlock을 다시 계산
	const finalAnchorBlock = anchorEl?.closest('.editor--block') as HTMLElement | null
	const finalFocusBlock = focusEl?.closest('.editor--block') as HTMLElement | null
	const blockEl = finalAnchorBlock

	// 테이블 블록에서는 td > div[contenteditable="true"] 내부만 허용
	if (finalAnchorBlock?.dataset.type === 'defaultTable') {
		if (!isValidTableCell(anchorEl)) {
			return false
		}
	}

	if (finalFocusBlock?.dataset.type === 'defaultTable') {
		if (!isValidTableCell(focusEl)) {
			return false
		}
	}

	emitBlockEditStart(blockEl)

	// 값 스타일(fontcolor 등)에서 선택이 깨진 경우 절대 오프셋으로 복원
	// 단, 멀티블록 선택일 때는 복원하지 않음 (단일 블록으로 축소되는 것을 방지)
	const isValueStyle = style === 'fontsize' || style === 'fontcolor' || style === 'backgroundcolor' || style === 'fontfamily'
	const saved = changeRange.value
	const isMultiBlockSelection = saved && saved.endBlockUid && saved.endBlockUid !== saved.blockUid
	
	if (isValueStyle && !isMultiBlockSelection && saved && blockEl && blockEl.dataset.type !== 'defaultTable' &&
		typeof saved.startAbs === 'number' && typeof saved.endAbs === 'number' &&
		saved.startAbs !== saved.endAbs && isSameBlockUid(blockEl, saved.blockUid)) {
		const currentLen = range.toString().length
		const savedLen = Math.max(0, saved.endAbs - saved.startAbs)
		if (savedLen > 0 && currentLen !== savedLen) {
			const absRange = buildRangeFromAbsOffsets(blockEl, saved.startAbs, saved.endAbs)
			if (absRange) {
				sel.removeAllRanges()
				sel.addRange(absRange)
				range = absRange
				anchorEl = absRange.startContainer.nodeType === Node.TEXT_NODE ? absRange.startContainer.parentElement : absRange.startContainer as HTMLElement | null
				focusEl = absRange.endContainer.nodeType === Node.TEXT_NODE ? absRange.endContainer.parentElement : absRange.endContainer as HTMLElement | null
			}
		}
	}

	// Range의 startContainer와 endContainer로 블록 재확인
	const startEl = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : (range.startContainer as HTMLElement)
	const endEl = range.endContainer.nodeType === Node.TEXT_NODE ? range.endContainer.parentElement : (range.endContainer as HTMLElement)
	const startBlock = startEl?.closest('.editor--block') as HTMLElement | null
	const endBlock = endEl?.closest('.editor--block') as HTMLElement | null

	// 선택이 단일 블록 내부인 경우 멀티블록 로직 건너뛰기
	const isSingleBlockSelection = startBlock && endBlock && startBlock === endBlock

	// range의 commonAncestorContainer 확인 (Cmd+A 같은 전체 선택 감지)
	const commonAncestor = range.commonAncestorContainer
	let ancestorEl = commonAncestor.nodeType === Node.TEXT_NODE ? commonAncestor.parentElement : (commonAncestor as HTMLElement)

	let blocksInRange: Element[] = []

	// 단일 블록 선택이 아닐 때만 범위 내 블록 탐색
	if (!isSingleBlockSelection) {
		// 블록을 찾을 때까지 상위로 올라감
		let loopCount = 0
		while (ancestorEl && !ancestorEl.classList?.contains('editor--container') && loopCount < 20) {
			const blocks = Array.from(ancestorEl.querySelectorAll('.editor--block'))
			if (blocks.length > 1) break
			ancestorEl = ancestorEl.parentElement
			loopCount++
		}
		const allFoundBlocks = ancestorEl ? Array.from(ancestorEl.querySelectorAll('.editor--block')) : []

		// Filter to only include blocks between startBlock and endBlock
		if (startBlock && endBlock && allFoundBlocks.length > 0) {
			const startIndex = allFoundBlocks.indexOf(startBlock)
			const endIndex = allFoundBlocks.indexOf(endBlock)

			if (startIndex !== -1 && endIndex !== -1) {
				const minIndex = Math.min(startIndex, endIndex)
				const maxIndex = Math.max(startIndex, endIndex)
				blocksInRange = allFoundBlocks.slice(minIndex, maxIndex + 1)
			}
		}
	}

	// .active 클래스를 가진 모든 블록 확인
	const activeBlocks = Array.from(document.querySelectorAll('.editor--block.active'))

	// 블록 경계를 넘는 선택 처리 (range 기준 또는 active 블록 기준)
	const useActiveBlocks = activeBlocks.length > 1
	const useRangeBlocks = blocksInRange.length > 1

	if (useActiveBlocks || useRangeBlocks || (!sel.isCollapsed && !range.collapsed && startBlock && endBlock && startBlock !== endBlock)) {
		// 두 블록에 걸친 선택: 각 블록의 선택 영역에 대해 서식 적용
		let allBlocks: HTMLElement[] = []

		if (useRangeBlocks) {
			// commonAncestor 안의 모든 블록 사용 (Cmd+A 등)
			allBlocks = (blocksInRange as HTMLElement[]).filter(b => b.dataset.type !== 'defaultTable')
		} else if (useActiveBlocks) {
			// active 클래스를 가진 블록들을 사용
			allBlocks = (activeBlocks as HTMLElement[]).filter(b => b.dataset.type !== 'defaultTable')
		} else {
			// range 기반으로 블록 수집
			let currentBlock = startBlock
			let blockLoopCount = 0
			// startBlock부터 endBlock까지의 모든 블록 수집
			while (currentBlock && blockLoopCount < 100) {
				if (currentBlock.dataset.type !== 'defaultTable') {
					allBlocks.push(currentBlock)
				}
				if (currentBlock === endBlock) break
				const container = currentBlock.closest('.editor--block__container')
				const nextContainer = container?.nextElementSibling
				if (nextContainer && nextContainer.classList.contains('editor--block__container')) {
					const nextBlock = nextContainer.querySelector('.editor--block') as HTMLElement | null
					if (nextBlock) {
						currentBlock = nextBlock
					} else {
						break
					}
				} else {
					break
				}
				blockLoopCount++
			}
		}

		// 멀티 블록 선택 시: 잠긴 블럭은 제외
		allBlocks = allBlocks.filter(b => !isBlockAvatarLocked(b))
		if (allBlocks.length === 0) return false

		// 멀티 블록 선택 시: 전체 영역의 스타일 비율을 계산하여 토글 결정
		let shouldApply = true
		const isValueStyle = style === 'fontsize' || style === 'fontcolor' || style === 'backgroundcolor' || style === 'fontfamily'

		// 값 스타일이 아닌 경우(bold, italic, underline 등)만 토글 상태 확인
		if (!isValueStyle) {
			let totalNodes = 0
			let styledNodes = 0

			// 모든 블록의 모든 텍스트 노드를 확인하여 스타일 비율 계산
			for (const block of allBlocks) {
				const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, null)
				let node
				while ((node = walker.nextNode())) {
					const text = node.textContent || ''
					if (!text || text.match(/^[\u200B-\u200D\uFEFF\u00A0\u2060]+$/)) continue

					totalNodes++
					const parentSpan = getClosestSpan(node)
					if (parentSpan && hasStyle(parentSpan, style, value)) {
						styledNodes++
					}
				}
			}

			// 50% 이상이 스타일을 가지고 있으면 제거, 아니면 적용
			if (totalNodes > 0 && styledNodes / totalNodes >= 0.5) {
				shouldApply = false
			}
		}

		// 각 블록에 대해 서식 적용
		allBlocks.forEach((block, index) => {
			if (!block.firstChild) return

			const isFirstBlock = index === 0
			const isLastBlock = index === allBlocks.length - 1

			const blockRange = document.createRange()

			try {
				if (isFirstBlock && isLastBlock) {
					blockRange.setStart(range.startContainer, range.startOffset)
					blockRange.setEnd(range.endContainer, range.endOffset)
				} else if (isFirstBlock) {
					blockRange.setStart(range.startContainer, range.startOffset)
					blockRange.setEndAfter(block.lastChild || block)
				} else if (isLastBlock) {
					blockRange.setStartBefore(block.firstChild || block)
					blockRange.setEnd(range.endContainer, range.endOffset)
				} else {
					blockRange.selectNodeContents(block)
				}

				const extracted = blockRange.extractContents()

				const processNode = (node: Node): Node => {
					if (node.nodeType === Node.TEXT_NODE) {
						const text = node.textContent || ''
						if (!text || text.match(/^[\u200B-\u200D\uFEFF\u00A0\u2060]+$/)) return node

						const newSpan = createSpan(text)
						if (isValueStyle || shouldApply) {
							applyStyleToSpan(newSpan, style, value)
						}
						return newSpan
					} else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'SPAN') {
						const span = node as HTMLSpanElement
						const children = Array.from(span.childNodes)
						span.textContent = ''
						children.forEach(child => {
							span.appendChild(processNode(child))
						})

						if (isValueStyle) {
							applyStyleToSpan(span, style, value)
						} else {
							if (shouldApply) {
								applyStyleToSpan(span, style, value)
							} else {
								removeStyleFromSpan(span, style, value)
							}
						}
						return span
					} else {
						if (node.hasChildNodes()) {
							const children = Array.from(node.childNodes)
							node.textContent = ''
							children.forEach(child => {
								node.appendChild(processNode(child))
							})
						}
						return node
					}
				}

				const processedFrag = document.createDocumentFragment()
				Array.from(extracted.childNodes).forEach(child => {
					processedFrag.appendChild(processNode(child))
				})

				blockRange.insertNode(processedFrag)
			} catch (e) {
			}

			normalizeBlock(block)
			saveBlockIfProject(block)
		})

		// 멀티 블록 서식 적용 직후 히스토리 push → baseline(_lastPushedBlocks) 즉시 갱신
		// saveBlockIfProject가 비동기 소켓 에코를 발생시키고,
		// modifyNoteBlockOn의 updateBaselineForRemoteModify가 baseline을 오염시키는 것을 방지
		const historyAfterMultiBlock = new UseHistory()
		await historyAfterMultiBlock.push(true, { fontStyleApplied: true, preserveSelectionOnUndo: true })

		// 멀티 블록 선택 후 선택 영역 복원
		new UseSelection().isCursorCheckPoint()

		return false
	}

	// 단일 커서 처리
	if (sel.isCollapsed || range.collapsed) {
		// 테이블 블록에서는 td > div[contenteditable="true"] 내부만 허용
		if (blockEl?.dataset.type === 'defaultTable') {
			if (!isValidTableCell(anchorEl)) {
				return false
			}
		}

		let carrier = getClosestSpan(range.startContainer)
		
		// INVISIBLE만 있는 span(캐럿 마커)은 분할 로직 대신 직접 스타일을 적용/토글한다.
		// - 이유: carrier 분할 로직에서 INVISIBLE 중복 생성 및 커서 튐 버그 발생
		// - 해결: 분할 없이 기존 스타일을 유지하면서 새 스타일을 직접 적용
		let handledInvisibleCarrier = false
		if (carrier) {
			const carrierText = carrier.textContent || ''
			if (!carrierText || carrierText === INVISIBLE || carrierText.match(/^[\u2060]+$/)) {
				const isValStyle = style === 'fontsize' || style === 'fontcolor' || style === 'backgroundcolor' || style === 'fontfamily'
				if (!isValStyle && hasStyle(carrier, style, value)) {
					removeStyleFromSpan(carrier, style, value)
				} else {
					applyStyleToSpan(carrier, style, value)
				}
				carrier.setAttribute('data-cursor-marker', 'true')
				handledInvisibleCarrier = true
				carrier = null
			}
		}
		
		const isValueStyle = style === 'fontsize' || style === 'fontcolor' || style === 'backgroundcolor' || style === 'fontfamily'

		if (handledInvisibleCarrier) {
			// INVISIBLE 캐럿 마커에 직접 스타일 적용 완료 → 아래 분기 건너뜀
		} else if (carrier && !isValueStyle && hasStyle(carrier, style, value)) {
			// 토글 스타일(bold, italic 등): 단일 커서 + 동일 스타일이면 커서 기준으로 쪼개고 해당 스타일 제거
			splitAtCaretAndToggleOff(carrier, range, style, value)
		} else if (carrier) {
			// carrier 내부에 커서가 있고 새 스타일 추가: carrier를 분할하고 기존 스타일 + 새 스타일 적용
			const full = carrier.textContent || ''

			let cutIndex = 0
			try {
				const pre = document.createRange()
				pre.setStart(carrier, 0)
				pre.setEnd(range.startContainer, range.startOffset)
				cutIndex = pre.toString().length
			} catch (e) {
				if (range.startContainer.nodeType === Node.TEXT_NODE && carrier.firstChild === range.startContainer) {
					cutIndex = range.startOffset
				}
			}
			cutIndex = Math.max(0, Math.min(cutIndex, full.length))

			const beforeText = full.slice(0, cutIndex)
			const afterText = full.slice(cutIndex)

			const carrierModel = StyleModel.fromSpan(carrier)
			const parent = carrier.parentNode
			if (!parent) return false

			const frag = document.createDocumentFragment()

			if (beforeText) {
				const beforeSpan = document.createElement('span')
				beforeSpan.textContent = beforeText
				carrierModel.applyToSpan(beforeSpan)
				frag.appendChild(beforeSpan)
			}

			const newModel = carrierModel.apply(style, value)
			const newSpan = document.createElement('span')
			newSpan.textContent = INVISIBLE
			newModel.applyToSpan(newSpan)
			newSpan.setAttribute('data-cursor-marker', 'true')
			frag.appendChild(newSpan)

			if (afterText) {
				const afterSpan = document.createElement('span')
				afterSpan.textContent = afterText
				carrierModel.applyToSpan(afterSpan)
				frag.appendChild(afterSpan)
			}

			parent.replaceChild(frag, carrier)
		} else {
			// 단일 커서 + 스타일 추가: 스타일 span(INVISIBLE 포함)을 삽입하고 커서를 내부로 이동
			const span = createSpan(INVISIBLE)
			applyStyleToSpan(span, style, value)
			span.setAttribute('data-cursor-marker', 'true')
			range.insertNode(span)
			// 커서 설정은 Line 1268-1283 markerSpan 로직에서 처리 (중복 방지)
		}

		if (blockEl) {
			const normalizeTarget = getNormalizeTarget(blockEl, anchorEl)
			
			await nextTick()
			
			const markerSpan = normalizeTarget?.querySelector('span[data-cursor-marker="true"]') as HTMLSpanElement | null

			if (markerSpan) {
				const textNode = markerSpan.firstChild
				if (textNode && textNode.nodeType === Node.TEXT_NODE) {
					const text = textNode.textContent || ''
					
					if (text.length > 0) {
						const offset = text.indexOf('\u2060')
						const targetOffset = offset >= 0 ? offset + 1 : 1
						const safeOffset = Math.max(0, Math.min(targetOffset, text.length))

						const sel = window.getSelection()
						if (sel) {
							const r = document.createRange()
							r.setStart(textNode, safeOffset)
							r.collapse(true)
							sel.removeAllRanges()
							sel.addRange(r)
						}
					}
				}
			}

			if (normalizeTarget) {
				normalizeBlock(normalizeTarget)
			}

			if (markerSpan && markerSpan.isConnected) {
				markerSpan.removeAttribute('data-cursor-marker')
			}

			saveBlockIfProject(blockEl)
		} else {
			saveBlockIfProject(null)
		}

		// Reset key count so that subsequent typing is treated as a single undo unit
		// Also set flag to skip the next history save (which would occur after 10 keystrokes)
		const keyManager = getKeyCountManager()
		keyManager.resetKeyCount()
		keyManager.setSkipNextHistorySave(true)
		return false
	}

	// 선택(드래그) 처리
	// 테이블 블록에서는 td > div[contenteditable="true"] 내부만 허용
	if (blockEl?.dataset.type === 'defaultTable') {
		if (!isValidTableCell(anchorEl) || !isValidTableCell(focusEl)) {
			return false
		}
	}

	const commonSpan = getClosestSpan(range.commonAncestorContainer)
	const selectedText = range.toString()
	if (!selectedText) return false

	// 1) span 1개가 전체 선택된 경우: span style만 변경/토글
	if (commonSpan) {
		const spanText = commonSpan.textContent || ''
		const isValueStyle = style === 'fontsize' || style === 'fontcolor' || style === 'backgroundcolor' || style === 'fontfamily'

		if (spanText === selectedText) {
			// 선택 영역에 마커 삽입 (normalizeBlock 후 복원을 위해)
			const sel = window.getSelection()
			const currentRange = sel?.rangeCount ? sel.getRangeAt(0) : null
			let startMarker: HTMLSpanElement | null = null
			let endMarker: HTMLSpanElement | null = null

			if (currentRange) {
				startMarker = document.createElement('span')
				startMarker.setAttribute('data-selection-start-marker', 'true')
				startMarker.textContent = INVISIBLE

				endMarker = document.createElement('span')
				endMarker.setAttribute('data-selection-end-marker', 'true')
				endMarker.textContent = INVISIBLE

				// startMarker 먼저 삽입 (선택 영역 시작)
				const startRange = currentRange.cloneRange()
				startRange.collapse(true)
				startRange.insertNode(startMarker)

				// DOM 변경 후 selection에서 다시 range 가져오기 (range 무효화 방지)
				const updatedRange = sel?.rangeCount ? sel.getRangeAt(0) : null
				if (!updatedRange) {
					// range를 가져올 수 없으면 startMarker도 제거하고 마커 없이 진행
					startMarker.parentNode?.removeChild(startMarker)
					startMarker = null
				} else {
					// endMarker 삽입 (선택 영역 끝)
					const endRange = updatedRange.cloneRange()
					endRange.collapse(false)
					endRange.insertNode(endMarker)
				}
			}

			// 값 스타일은 항상 덮어쓰기 (토글 없음)
			if (isValueStyle) {
				applyStyleToSpan(commonSpan, style, value)
			} else if (hasStyle(commonSpan, style, value)) {
				// 토글 스타일: 이미 있으면 제거
				removeStyleFromSpan(commonSpan, style, value)
				// 스타일이 비었으면 span 제거(중첩 방지/DOM 정리)
				const model = StyleModel.fromSpan(commonSpan)
				if (model.isEmpty()) {
					removeSpan(commonSpan)
				}
			} else {
				// 토글 스타일: 없으면 추가
				applyStyleToSpan(commonSpan, style, value)
			}
			if (blockEl) {
				const normalizeTarget = getNormalizeTarget(blockEl, anchorEl)
				if (normalizeTarget) {
					normalizeBlock(normalizeTarget)
				}
				saveBlockIfProject(blockEl)
			} else {
				saveBlockIfProject(null)
			}

			// 마커 기반 선택 복원
			const searchTarget = getNormalizeTarget(blockEl, anchorEl) || blockEl

			if (searchTarget && startMarker && endMarker) {
				const foundStartMarker = searchTarget.querySelector('[data-selection-start-marker]') as HTMLSpanElement | null
				const foundEndMarker = searchTarget.querySelector('[data-selection-end-marker]') as HTMLSpanElement | null

				if (foundStartMarker && foundEndMarker) {
					try {
						const r = document.createRange()

						// 마커 바로 다음부터 선택 시작
						r.setStartAfter(foundStartMarker)

						// 마커 바로 전까지 선택 종료
						r.setEndBefore(foundEndMarker)

						const s = window.getSelection()
						if (s) {
							s.removeAllRanges()
							s.addRange(r)
							markerRestoredSuccessfully = true

							// DOM 변경 전에 복원된 selection 저장
							new UseSelection().setChangeSelection()
						}

						// 마커 제거
						foundStartMarker.parentNode?.removeChild(foundStartMarker)
						foundEndMarker.parentNode?.removeChild(foundEndMarker)

						// DOM 안정화 대기
						await nextTick()
					} catch (e) {
						// 복원 실패 시 마커만 제거
						foundStartMarker.parentNode?.removeChild(foundStartMarker)
						foundEndMarker.parentNode?.removeChild(foundEndMarker)
					}
				} else {
					// 마커를 찾지 못한 경우: 마커가 있다면 제거
					if (foundStartMarker) foundStartMarker.parentNode?.removeChild(foundStartMarker)
					if (foundEndMarker) foundEndMarker.parentNode?.removeChild(foundEndMarker)
				}
			}

			// 마커 복원 실패 시 fallback으로 isCursorCheckPoint 호출
			if (!markerRestoredSuccessfully) {
				await nextTick()
				new UseSelection().isCursorCheckPoint()
			}

			const historyAfterSpan = new UseHistory()
			await historyAfterSpan.push(false, { isUndoRedoAction: true })

			return markerRestoredSuccessfully
		}
	}

	// 2) 부분 선택: 기존 span 스타일을 유지하면서, 선택 영역만 새 span(기존+신규 스타일)로 감싼다.
	// - md 추가 규칙 반영:
	//   - 드래그한 텍스트는 span으로 감싸져야 한다.
	//   - 새 span이 추가되면 기존 드래그 영역은 사라져야 한다. (extractContents로 제거 후 재삽입)

	// LBI-481/LBI-497 수정: commonSpan 내 부분 선택 시 span을 명시적으로 분할
	if (commonSpan && commonSpan.textContent && commonSpan.textContent.includes(selectedText) && commonSpan.textContent !== selectedText) {
		const spanFullText = commonSpan.textContent
		const startIdx = spanFullText.indexOf(selectedText)
		const endIdx = startIdx + selectedText.length

		const beforeText = spanFullText.slice(0, startIdx)
		const middleText = spanFullText.slice(startIdx, endIdx)
		const afterText = spanFullText.slice(endIdx)

		const origModel = StyleModel.fromSpan(commonSpan)
		const parent = commonSpan.parentNode
		if (!parent) return false

		const frag = document.createDocumentFragment()

		// before 부분: 원래 스타일 유지
		if (beforeText) {
			const beforeSpan = document.createElement('span')
			beforeSpan.textContent = beforeText
			origModel.applyToSpan(beforeSpan)
			frag.appendChild(beforeSpan)
		}

		// 시작 마커 삽입
		const startMarker = document.createElement('span')
		startMarker.setAttribute('data-selection-start-marker', 'true')
		startMarker.textContent = INVISIBLE
		frag.appendChild(startMarker)

		// middle 부분: 스타일 적용/토글
		const isValueStyle = style === 'fontsize' || style === 'fontcolor' || style === 'backgroundcolor' || style === 'fontfamily'
		let middleModel: StyleModel

		if (isValueStyle) {
			// 값 스타일은 항상 덮어쓰기 (토글 없음)
			middleModel = origModel.apply(style, value)
		} else {
			// 토글 스타일: 있으면 제거, 없으면 추가
			const wasToggleOff = origModel.hasStyle(style, value)
			middleModel = wasToggleOff ? origModel.remove(style, value) : origModel.apply(style, value)
		}

		// 계산된 스타일로 middle span 생성
		// 스타일이 비어있으면 텍스트로만 삽입
		if (middleModel.isEmpty()) {
			frag.appendChild(document.createTextNode(middleText))
		} else {
			const middleSpan = document.createElement('span')
			middleSpan.textContent = middleText
			middleModel.applyToSpan(middleSpan)
			frag.appendChild(middleSpan)
		}

		// 끝 마커 삽입
		const endMarker = document.createElement('span')
		endMarker.setAttribute('data-selection-end-marker', 'true')
		endMarker.textContent = INVISIBLE
		frag.appendChild(endMarker)

		// after 부분: 원래 스타일 유지
		if (afterText) {
			const afterSpan = document.createElement('span')
			afterSpan.textContent = afterText
			origModel.applyToSpan(afterSpan)
			frag.appendChild(afterSpan)
		}

		parent.replaceChild(frag, commonSpan)

		if (blockEl) {
			const normalizeTarget = getNormalizeTarget(blockEl, anchorEl)
			if (normalizeTarget) {
				normalizeBlock(normalizeTarget)
			}
			saveBlockIfProject(blockEl)
		} else {
			saveBlockIfProject(null)
		}

		// 마커 기반 선택 복원
		const searchTarget = getNormalizeTarget(blockEl, anchorEl) || blockEl
		if (searchTarget) {
			const foundStartMarker = searchTarget.querySelector('[data-selection-start-marker]') as HTMLSpanElement | null
			const foundEndMarker = searchTarget.querySelector('[data-selection-end-marker]') as HTMLSpanElement | null

			if (foundStartMarker && foundEndMarker) {
				try {
					// 마커 사이의 모든 텍스트 노드를 찾아서 선택하는 개선된 로직
					const r = document.createRange()
					r.setStartAfter(foundStartMarker)
					r.setEndBefore(foundEndMarker)

					// Range의 commonAncestorContainer 내에서 실제 텍스트 노드 찾기
					const walker = document.createTreeWalker(r.commonAncestorContainer, NodeFilter.SHOW_TEXT, {
						acceptNode: node => {
							const text = node.textContent || ''
							if (!text || text.match(/^[\u200B-\u200D\uFEFF\u00A0\u2060]+$/)) {
								return NodeFilter.FILTER_REJECT
							}
							if (r.intersectsNode(node)) {
								return NodeFilter.FILTER_ACCEPT
							}
							return NodeFilter.FILTER_REJECT
						},
					})

					let firstTextNode: Text | null = null
					let lastTextNode: Text | null = null
					let node = walker.nextNode() as Text | null

					while (node) {
						if (!firstTextNode) firstTextNode = node
						lastTextNode = node
						node = walker.nextNode() as Text | null
					}

					// 텍스트 노드를 찾았으면 정확하게 설정
					if (firstTextNode && lastTextNode) {
						r.setStart(firstTextNode, 0)
						r.setEnd(lastTextNode, lastTextNode.length)
					}

					// 마커 제거 전에 텍스트 노드 참조 저장
					const savedFirstTextNode = firstTextNode
					const savedLastTextNode = lastTextNode

					// 마커 제거
					foundStartMarker.parentNode?.removeChild(foundStartMarker)
					foundEndMarker.parentNode?.removeChild(foundEndMarker)

					// DOM 안정화 대기
					await nextTick()

					// 마커 제거 후 선택 복원
					if (savedFirstTextNode && savedLastTextNode) {
						const finalRange = document.createRange()
						finalRange.setStart(savedFirstTextNode, 0)
						finalRange.setEnd(savedLastTextNode, savedLastTextNode.length)

						const s = window.getSelection()
						if (s) {
							s.removeAllRanges()
							s.addRange(finalRange)
							markerRestoredSuccessfully = true

							// 최종 선택 영역 저장
							new UseSelection().setChangeSelection()
						}
					}
				} catch (e) {
					// 복원 실패 시 마커만 제거
					foundStartMarker.parentNode?.removeChild(foundStartMarker)
					foundEndMarker.parentNode?.removeChild(foundEndMarker)
				}
			} else {
				// 마커를 찾지 못한 경우: 마커가 있다면 제거
				if (foundStartMarker) foundStartMarker.parentNode?.removeChild(foundStartMarker)
				if (foundEndMarker) foundEndMarker.parentNode?.removeChild(foundEndMarker)
			}
		}

		// 마커 복원 실패 시 fallback으로 isCursorCheckPoint 호출
		if (!markerRestoredSuccessfully) {
			await nextTick()
			new UseSelection().isCursorCheckPoint()
		}

		const historyAfterPartial = new UseHistory()
		await historyAfterPartial.push(false, { isUndoRedoAction: true })

		return markerRestoredSuccessfully
	}

	// 마커 삽입 (extractContents 경로)
	let startMarker: HTMLSpanElement | null = null
	let endMarker: HTMLSpanElement | null = null

	const baseSpan = getClosestSpan(range.startContainer)
	const baseSig: SpanSignature = baseSpan ? signatureFromSpan(baseSpan) : { model: new StyleModel(), attrs: {} }

	// extractContents 실행 (마커 삽입 전)
	const extracted = range.extractContents()
	const built = buildStyledFragmentFromExtracted(extracted, style, value, baseSig)

	// extractContents 후 마커 삽입
	startMarker = document.createElement('span')
	startMarker.setAttribute('data-selection-start-marker', 'true')
	startMarker.textContent = INVISIBLE

	endMarker = document.createElement('span')
	endMarker.setAttribute('data-selection-end-marker', 'true')
	endMarker.textContent = INVISIBLE

	// 마커와 built.frag를 하나의 fragment로 결합
	const finalFrag = document.createDocumentFragment()
	finalFrag.appendChild(startMarker)
	// built.frag의 모든 자식을 finalFrag로 이동
	while (built.frag.firstChild) {
		finalFrag.appendChild(built.frag.firstChild)
	}
	finalFrag.appendChild(endMarker)

	const insertContainer = range.startContainer
	const insertSpan = getClosestSpan(insertContainer)
	if (insertSpan && insertContainer.nodeType === Node.TEXT_NODE) {
		const textNode = insertContainer as Text
		const offset = range.startOffset

		if (offset > 0 && offset < textNode.length) {
			const afterText = textNode.splitText(offset)

			const beforeSpan = insertSpan
			const afterSpan = beforeSpan.cloneNode(false) as HTMLSpanElement

			afterSpan.appendChild(afterText)
			beforeSpan.parentNode?.insertBefore(afterSpan, beforeSpan.nextSibling)
			beforeSpan.parentNode?.insertBefore(finalFrag, afterSpan)
		} else if (offset === 0) {
			insertSpan.parentNode?.insertBefore(finalFrag, insertSpan)
		} else {
			insertSpan.parentNode?.insertBefore(finalFrag, insertSpan.nextSibling)
		}
	} else {
		range.insertNode(finalFrag)
	}

	// 커서를 삽입된 영역 내부/끝으로 이동
	// 단, 마커가 존재하면 마커 복원에만 의존하고 커서를 조작하지 않음
	if (built.lastNode && !(startMarker && endMarker)) {
		const s = window.getSelection()
		if (s) {
			const r = document.createRange()
			// 텍스트 노드면 내부 끝, 요소면 선택 유지
			if (built.lastNode.nodeType === Node.TEXT_NODE) {
				const textNode = built.lastNode as Text
				r.setStart(textNode, textNode.length)
				r.setEnd(textNode, textNode.length)
			} else if (built.lastNode.nodeType === Node.ELEMENT_NODE) {
				const el = built.lastNode as HTMLElement
				if (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE) {
					const textNode = el.firstChild as Text
					r.setStart(textNode, textNode.length)
					r.setEnd(textNode, textNode.length)
				} else {
					r.selectNodeContents(el)
					r.collapse(false)
				}
			} else {
				r.setStartAfter(built.lastNode)
				r.collapse(true)
			}
			s.removeAllRanges()
			s.addRange(r)
		}
	}

	if (blockEl) {
		const normalizeTarget = getNormalizeTarget(blockEl, anchorEl)
		if (normalizeTarget) {
			normalizeBlock(normalizeTarget)
			;(window as any).__DEBUG_LAST_BLOCK_HTML = normalizeTarget.innerHTML
		}
		saveBlockIfProject(blockEl)

		// 마커 기반 선택 복원 (extractContents 경로)
		const searchTarget = getNormalizeTarget(blockEl, anchorEl) || blockEl

		if (searchTarget && startMarker && endMarker) {
			const foundStartMarker = searchTarget.querySelector('[data-selection-start-marker]') as HTMLSpanElement | null
			const foundEndMarker = searchTarget.querySelector('[data-selection-end-marker]') as HTMLSpanElement | null

			if (foundStartMarker && foundEndMarker) {
				try {
					const r = document.createRange()

					r.setStartAfter(foundStartMarker)
					r.setEndBefore(foundEndMarker)

					const s = window.getSelection()
					if (s) {
						s.removeAllRanges()
						s.addRange(r)
						markerRestoredSuccessfully = true

						// DOM 변경 전에 복원된 selection 저장
						new UseSelection().setChangeSelection()
					}

					foundStartMarker.parentNode?.removeChild(foundStartMarker)
					foundEndMarker.parentNode?.removeChild(foundEndMarker)

					// DOM 안정화 대기
					await nextTick()
				} catch (e) {
					foundStartMarker.parentNode?.removeChild(foundStartMarker)
					foundEndMarker.parentNode?.removeChild(foundEndMarker)
				}
			} else {
				// 마커를 찾지 못한 경우: 마커가 있다면 제거
				if (foundStartMarker) foundStartMarker.parentNode?.removeChild(foundStartMarker)
				if (foundEndMarker) foundEndMarker.parentNode?.removeChild(foundEndMarker)
			}
		}

		// 마커 복원 실패 시 fallback으로 isCursorCheckPoint 호출
		if (!markerRestoredSuccessfully) {
			await nextTick()
			new UseSelection().isCursorCheckPoint()
		}

		const historyAfter = new UseHistory()
		await historyAfter.push(false, { isUndoRedoAction: true })

		return markerRestoredSuccessfully
	} else {
		saveBlockIfProject(null)
	}

	// 마커 복원이 실패했거나 마커가 없는 경우에만 isCursorCheckPoint 호출
	if (!markerRestoredSuccessfully) {
		new UseSelection().isCursorCheckPoint()
	}

	const history = new UseHistory()
	await history.push(false, { isUndoRedoAction: true })

	// markerRestoredSuccessfully 반환 (Options.vue에서 조건부 처리용)
	return markerRestoredSuccessfully
}
